<script lang="ts">
    import { onDestroy, tick } from 'svelte'
    import { fade } from 'svelte/transition'

    import Icon from '@iconify/svelte'

    import { gettext as _ } from 'hydria/services/gettext'
    import { signedIn } from 'hydria/services/session'

    import { helpDocument, load as loadHelp } from './help'
    import type { HeaderData } from './help'

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    let status: 'closed' | 'loading' | 'open' | 'not-found' = $state('closed')

    // Currently loaded document ID
    let tag: string | undefined = undefined
    // header to scroll to
    let hashTag: string | undefined = undefined
    // @ts-expect-error  - parseInt(null) causes a NaN, we are OK with that
    let fontSize = $state(parseInt(window.localStorage.getItem('help-font-size'), 10))
    let fontStyle = $state(window.localStorage.getItem('help-font-style'))

    if (fontSize == null || Number.isNaN(fontSize) || fontSize < 1 || fontSize > 5) fontSize = 3
    if (!fontStyle || !['serif', 'sans'].includes(fontStyle)) fontStyle = 'serif'

    function decFontSize() {
        if (fontSize > 1) fontSize--
    }
    function incFontSize() {
        if (fontSize < 5) fontSize++
    }
    function setFontStyle(style: 'serif' | 'sans') {
        if (['serif', 'sans'].includes(style) && style != fontStyle) fontStyle = style
    }
    function toggleFontStyle() {
        fontStyle = fontStyle == 'serif' ? 'sans' : 'serif'
    }

    let title: string | undefined = $state(undefined)
    let html: string | undefined = $state(undefined)

    let headers: HeaderData[] = $state([])
    let headerIndex: { [key: string]: HeaderData } = {}

    function close() {
        status = 'closed'
        title = undefined
        html = undefined
        headers = []
        headerIndex = {}
        tag = undefined
        hashTag = undefined
        if ($helpDocument != null) helpDocument.set(null)
    }

    async function load(what: string) {
        if (what == tag) {
            return
        }

        if (what.match(/#/)) {
            ;[tag, hashTag] = what.split('#', 2)
        } else {
            tag = what
        }

        status = 'loading'

        const data = await loadHelp(tag)
        // const res = await simpleFetch('GET', `/json/general/1/help/${$lg}/${encodeURIComponent(what)}`)

        if (data == null) {
            status = 'not-found'
            return
        }

        title = data.title
        html = data.html
        headers = data.headers
        headerIndex = {}

        for (const h of headers) {
            headerIndex[h.id] = h
        }

        status = 'open'

        if (hashTag != null) {
            if (!hashTag.startsWith('help-')) hashTag = `help-${hashTag}`
            if (headerIndex[hashTag] == null) hashTag = undefined
        }

        tick().then(() => {
            if (hashTag != null) {
                scrollTo(hashTag, false)
            } else {
                const el = document.getElementById('help-html')
                if (el) el.scrollTop = 0
            }
        })
    }

    // onMount(() => {
    //     if (hashTag != null) scrollTo(hashTag)
    //     else {
    //         document.getElementById('help-html').scrollTop = 0
    //     }
    // })

    function scrollTo(hId: string, smooth = true) {
        const element = document.getElementById(hId)
        if (!element) {
            return
        }

        element.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' })
    }

    unsubs.push(
        helpDocument.subscribe((nv) => {
            if (nv == null) {
                close()
            } else {
                load(nv)
            }
        }),
    )

    $effect(() => {
        if (!$signedIn && status != 'closed') close()
    })
    $effect(() => {
        window.localStorage.setItem('help-font-size', `${fontSize}`)
    })
    $effect(() => {
        window.localStorage.setItem('help-font-style', `${fontStyle}`)
    })
    $effect(() => {
        if (status != 'closed' && !document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.add('modal-open')
        } else if (status == 'closed' && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })
</script>

<svelte:window
    onkeydown={(e) => {
        if (e.key == 'Escape') close()
    }}
/>

{#if status != 'closed'}
    <div id="help-modal" transition:fade={{ duration: 125 }} class="modal help-modal" role="dialog">
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-body modal-help-body" id={status == 'open' ? 'modal-help-body' : ''}>
                    {#if status == 'loading'}
                        <div class="help-status">
                            <div class="vpad"></div>
                            <div class="status">
                                <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                                {$_('Loading…')}
                            </div>
                            <div class="vpad"></div>
                        </div>
                    {:else if status == 'not-found'}
                        <div class="help-status">
                            <div class="vpad"></div>
                            <div class="status">{$_('Could not find requested documentation.')}</div>
                            <div class="vpad"></div>
                        </div>
                    {:else}
                        <div class="help-grid fnt-{fontSize} fnt-{fontStyle}">
                            <div class="toc-actions">
                                <div class="btn-group btn-group-sm">
                                    <button class="btn btn-outline-secondary" type="button" onclick={decFontSize} disabled={fontSize < 2}><Icon inline icon="fa:minus" /></button>
                                    <button class="btn btn-outline-secondary" type="button" onclick={toggleFontStyle}><Icon inline icon="fa:font" /></button>
                                    <button class="btn btn-outline-secondary" type="button" onclick={incFontSize} disabled={fontSize > 4}><Icon inline icon="fa:plus" /></button>
                                </div>
                            </div>
                            <div class="toc-close">
                                <a
                                    href="/"
                                    onclick={(e) => {
                                        e.preventDefault()
                                        close()
                                    }}>{$_('Close')}</a
                                >
                            </div>
                            <div class="toc-title">{$_('Table of contents')}</div>
                            <div class="toc-content">
                                {#each headers as h (h.id)}
                                    <div class="toc-element level-{h.level}">
                                        <a
                                            href="/"
                                            onclick={(e) => {
                                                e.preventDefault()
                                                scrollTo(h.id)
                                            }}>{h.title}</a
                                        >
                                    </div>
                                {/each}
                            </div>

                            <div class="help-title">
                                <div>
                                    <Icon inline icon="fa:question-circle" />
                                    {title}
                                </div>
                            </div>
                            <div class="help-area" id="help-html"><div class="help-html">{@html html}</div></div>
                        </div>

                        <!-- <div class="toc">
                            <div class="toc-pre-actions">
                                <div>
                                    <div class="btn-group btn-group-sm">
                                        <button class="btn btn-outline-secondary" type="button" on:click={decFontSize} disabled={fontSize < 2}><Icon inline icon="fa:minus" /></button>
                                        <button class="btn btn-outline-secondary" type="button" on:click={toggleFontStyle}><Icon inline icon="fa:font" /></button>
                                        <button class="btn btn-outline-secondary" type="button" on:click={incFontSize} disabled={fontSize > 4}><Icon inline icon="fa:plus" /></button>
                                    </div>
                                </div>
                                <div class="spacer" />
                                <div><a href="/" on:click|preventDefault={() => close()}>{$_('Close')}</a></div>
                            </div>
                            <div class="toc-title">{$_('Table of contents')}</div>
                            <div class="toc-content">
                                {#each headers as h (h.id)}
                                    <div class="toc-element level-{h.level}">
                                        <a href="/" on:click|preventDefault={() => scrollTo(h.id)}>{h.title}</a>
                                    </div>
                                {/each}
                            </div>
                        </div> -->

                        <!-- <div class="help-left fnt-{fontSize} fnt-{fontStyle}">
                            <div class="help-title"><Icon inline icon="fa:question-circle" /> {title}</div>
                            <div class="help-html" id="help-html">{@html html}</div>
                        </div> -->
                    {/if}
                </div>
            </div>
        </div>
        <div class="modal-backdrop" onclick={() => close()} aria-hidden="true"></div>
    </div>
{/if}
